import React, { useState,useRef  } from 'react';
import { ArrowLeft, Database, Shield, HardDrive,AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const RDSPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { rdsData, region, accountName, duration } = location.state || {};


  const LongTextCell = ({ text, maxLength = 30 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const cellRef = useRef(null);
    
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }
    
    const handleMouseEnter = (e) => {
      setShowTooltip(true);
      setTimeout(() => {
        if (cellRef.current && tooltipRef.current) {
          const cellRect = cellRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - cellRect.bottom;
          const tooltipHeight = tooltipRect.height;
          
          if (spaceBelow < tooltipHeight) {
            setTooltipPosition({
              top: -tooltipHeight - 5,
              left: 0
            });
          } else {
            setTooltipPosition({
              top: "100%",
              left: 0
            });
          }
        }
      }, 0);
    };
  
    return (
      <div className="relative inline-block">
        <div
          ref={cellRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-pointer border-b border-dotted border-gray-400"
        >
          {text.substring(0, maxLength)}...
        </div>
        {showTooltip && (
          <div 
            ref={tooltipRef}
            className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              maxWidth: "250px",
              wordBreak: "break-word"
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };
  const ErrorStatus = ({ errorCode, errorMessage }) => {
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          errorCode ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {errorCode ? 'Error' : 'Success'}
        </span>
        {errorMessage && (
          <CustomTooltip text={errorMessage} position="right" />
        )}
      </div>
    );
  };

   const CustomTooltip = ({ text, position = 'left' }) => {
      const [showTooltip, setShowTooltip] = useState(false);
    
      return (
        <div className="relative inline-block">
          <div
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="cursor-pointer text-xs text-red-600 hover:text-red-800"
          >
            Error details
          </div>
          {showTooltip && (
            <div className={`absolute z-50 w-96 p-3 text-sm bg-gray-900 text-white rounded shadow-lg -mt-1 ${position === 'right' ? 'right-0' : 'left-0'}`}>
              <div className="whitespace-normal break-words">{text}</div>
            </div>
          )}
        </div>
      );
    };
  // Calculate summary statistics
  const calculateSummary = (data) => {
    if (!data) return {};
    
    const summary = {
      createCount: 0,
      deleteCount: 0,
      startCount: 0,
      stopCount: 0,
      modifyCount: 0,
      totalInstances: new Set(),
      engineCounts: {},
      instanceClassCounts: {}
    };

    data.forEach(event => {
      switch (event.eventname) {
        case 'CreateDBInstance':
          summary.createCount++;
          break;
        case 'DeleteDBInstance':
          summary.deleteCount++;
          break;
        case 'StartDBInstance':
          summary.startCount++;
          break;
        case 'StopDBInstance':
          summary.stopCount++;
          break;
        case 'ModifyDBInstance':
          summary.modifyCount++;
          break;
      }
      if (event.db_instance) summary.totalInstances.add(event.db_instance);
      
      // Count DB engines
      if (event.db_engine) {
        if(event.db_engine != "N/A"){
        summary.engineCounts[event.db_engine] = (summary.engineCounts[event.db_engine] || 0) + 1;
        }
      }
      
      // Count instance classes
      if (event.instance_class) {
        if(event.instance_class != "N/A"){
        summary.instanceClassCounts[event.instance_class] = (summary.instanceClassCounts[event.instance_class] || 0) + 1;
      }
    }
    });

    return {
      ...summary,
      totalInstances: summary.totalInstances.size,
    };
  };

  const summary = calculateSummary(rdsData?.data);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">RDS Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Instances</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.totalInstances || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Operations</div>
            <div className="text-xl font-semibold text-[#003366]">
              {(summary.createCount || 0) + 
               (summary.deleteCount || 0) + 
               (summary.modifyCount || 0) + 
               (summary.startCount || 0) + 
               (summary.stopCount || 0)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Instance Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{summary.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{summary.deleteCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Modified</span>
              <span className="font-semibold">{summary.modifyCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">DB Engines</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Unique Engines</span>
              <span className="font-semibold">{Object.keys(summary.engineCounts || {}).length}</span>
            </div>
            <div className="border-t border-gray-200 mt-2 pt-2">
              {Object.entries(summary.engineCounts || {}).map(([engine, count]) => (
                <div key={engine} className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">{engine}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <HardDrive className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Instance Classes</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Instance Classes</span>
              <span className="font-semibold">{Object.keys(summary.instanceClassCounts || {}).length}</span>
            </div>
            <div className="border-t border-gray-200 mt-2 pt-2">
              {Object.entries(summary.instanceClassCounts || {}).map(([instanceClass, count]) => (
                <div key={instanceClass} className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">{instanceClass}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-[#003366] mb-4">RDS Events</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider w-40">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    DB Instance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    Instance Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    Instance Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    DB Engine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider">
                    Source IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap uppercase tracking-wider w-56">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rdsData?.data && rdsData.data.length > 0 ? ( rdsData?.data?.map((event, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.username} />
                      </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.eventname} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.db_instance} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.instance_status}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.instance_class} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.db_engine} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.sourceipaddress}
                    </td>
                    <td className="px-6 py-4">
                        <ErrorStatus 
                          errorCode={event.errorcode}
                          errorMessage={event.errormessage}
                        />
                      </td>
            
                  </tr>
                ))):<tr>

                <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
          
                  <div className="flex flex-col items-center justify-center">
          
                    <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
          
                    <p className="text-lg font-medium">No data to display</p>
          
                    <p className="text-sm">No rds events were found for the selected time period and filters.</p>
          
                  </div>
          
                </td>
          
              </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RDSPage;